import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { validateVariantIds } from '../utils/shopify-storefront';
import { parseStoredCartItems, MAX_CART_QUANTITY } from '../utils/cart-storage';
import type { CartItem } from '../utils/cart-storage';
import { isSafeCheckoutUrl } from '../utils/checkout-url';

export type { CartItem };

interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  addItem: (item: Omit<CartItem, 'quantity'>, quantity: number) => void;
  removeItem: (variantId: string) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  openCart: () => void;
  closeCart: () => void;
  totalItems: number;
  totalPrice: number;
  checkoutUrl: string | null;
  isShopifyConnected: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const SHOPIFY_DOMAIN = (process.env.GATSBY_SHOPIFY_STORE_DOMAIN || '')
  .replace(/^https?:\/\//, '')
  .replace(/\/$/, '');
const SHOPIFY_TOKEN = process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN || '';
const STOREFRONT_API_VERSION = '2024-10';
const CART_STORAGE_KEY = 'tsts-cart-items';
const CART_ID_STORAGE_KEY = 'tsts-cart-id';

const isShopifyConfigured = Boolean(SHOPIFY_DOMAIN && SHOPIFY_TOKEN);

const storefrontFetch = async (query: string, variables: Record<string, unknown>) => {
  const res = await fetch(
    `https://${SHOPIFY_DOMAIN}/api/${STOREFRONT_API_VERSION}/graphql.json`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    }
  );
  if (!res.ok) throw new Error(`Storefront API ${res.status}: ${await res.text()}`);
  const json = await res.json();
  if (json.errors) throw new Error(`Storefront API errors: ${JSON.stringify(json.errors)}`);
  return json.data;
};

const CART_FRAGMENT = `
  id
  checkoutUrl
  totalQuantity
`;

const cartCreate = async (lines: { merchandiseId: string; quantity: number }[]) => {
  const data = await storefrontFetch(
    `mutation($lines: [CartLineInput!]) {
      cartCreate(input: { lines: $lines }) {
        cart { ${CART_FRAGMENT} }
        userErrors { code field message }
      }
    }`,
    { lines }
  );
  return data.cartCreate.cart;
};

const cartLinesUpdate = async (cartId: string, lines: { merchandiseId: string; quantity: number }[]) => {
  // Use cartLinesAdd/Update via simpler approach: replace by recreating
  // Storefront API requires line ids for update; simplest is to fetch existing lines
  const cart = await storefrontFetch(
    `query($id: ID!) {
      cart(id: $id) {
        id
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
    }`,
    { id: cartId }
  );
  if (!cart.cart) return null;

  const existingLines: { id: string; merchandiseId: string }[] = cart.cart.lines.edges.map(
    (e: any) => ({ id: e.node.id, merchandiseId: e.node.merchandise.id })
  );

  const desiredByMerchandise = new Map(lines.map((l) => [l.merchandiseId, l.quantity]));

  const removeIds = existingLines
    .filter((l) => !desiredByMerchandise.has(l.merchandiseId))
    .map((l) => l.id);

  const updateLines = existingLines
    .filter((l) => desiredByMerchandise.has(l.merchandiseId))
    .map((l) => ({ id: l.id, quantity: desiredByMerchandise.get(l.merchandiseId)! }));

  const existingMerchandiseSet = new Set(existingLines.map((l) => l.merchandiseId));
  const addLines = lines.filter((l) => !existingMerchandiseSet.has(l.merchandiseId));

  if (removeIds.length > 0) {
    await storefrontFetch(
      `mutation($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart { ${CART_FRAGMENT} }
          userErrors { code field message }
        }
      }`,
      { cartId, lineIds: removeIds }
    );
  }
  if (updateLines.length > 0) {
    await storefrontFetch(
      `mutation($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart { ${CART_FRAGMENT} }
          userErrors { code field message }
        }
      }`,
      { cartId, lines: updateLines }
    );
  }
  if (addLines.length > 0) {
    await storefrontFetch(
      `mutation($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart { ${CART_FRAGMENT} }
          userErrors { code field message }
        }
      }`,
      { cartId, lines: addLines }
    );
  }

  const refreshed = await storefrontFetch(
    `query($id: ID!) { cart(id: $id) { ${CART_FRAGMENT} } }`,
    { id: cartId }
  );
  return refreshed.cart;
};

const fetchCart = async (cartId: string) => {
  const data = await storefrontFetch(
    `query($id: ID!) { cart(id: $id) { ${CART_FRAGMENT} } }`,
    { id: cartId }
  );
  return data.cart;
};

const loadCartFromStorage = (): CartItem[] => {
  if (typeof window === 'undefined') return [];
  try {
    return parseStoredCartItems(localStorage.getItem(CART_STORAGE_KEY));
  } catch {
    return [];
  }
};

const acceptCheckoutUrl = (url: unknown): string | null =>
  isSafeCheckoutUrl(url) ? url : null;

const saveCartToStorage = (items: CartItem[]) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {}
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [cartId, setCartId] = useState<string | null>(null);

  const isShopifyConnected = isShopifyConfigured;

  useEffect(() => {
    setItems(loadCartFromStorage());
    if (typeof window !== 'undefined') {
      const storedCartId = localStorage.getItem(CART_ID_STORAGE_KEY);
      if (storedCartId) setCartId(storedCartId);
    }
  }, []);

  useEffect(() => {
    saveCartToStorage(items);
  }, [items]);

  useEffect(() => {
    if (!isShopifyConfigured || !cartId) return;
    fetchCart(cartId)
      .then((cart) => {
        if (cart) {
          setCheckoutUrl(acceptCheckoutUrl(cart.checkoutUrl));
        } else {
          localStorage.removeItem(CART_ID_STORAGE_KEY);
          localStorage.removeItem(CART_STORAGE_KEY);
          setCartId(null);
          setCheckoutUrl(null);
          setItems([]);
        }
      })
      .catch(() => {
        localStorage.removeItem(CART_ID_STORAGE_KEY);
        setCartId(null);
      });
  }, [cartId]);

  const syncToShopify = useCallback(async (updatedItems: CartItem[]) => {
    if (!isShopifyConfigured) return;
    try {
      let validItems = updatedItems;
      if (updatedItems.length > 0) {
        const { valid } = await validateVariantIds(
          updatedItems.map((i) => i.variantId)
        );
        validItems = updatedItems.filter((i) => valid.has(i.variantId));
        if (validItems.length !== updatedItems.length) {
          const removed = updatedItems
            .filter((i) => !valid.has(i.variantId))
            .map((i) => i.productTitle);
          console.warn(
            `Removed unavailable items from cart: ${removed.join(', ')}`
          );
          setItems(validItems);
          if (typeof window !== 'undefined') {
            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(validItems));
          }
        }
      }

      const lines = validItems.map((item) => ({
        merchandiseId: item.variantId,
        quantity: item.quantity,
      }));

      let cart;
      const existingId =
        typeof window !== 'undefined'
          ? localStorage.getItem(CART_ID_STORAGE_KEY)
          : null;

      if (lines.length === 0) {
        setCheckoutUrl(null);
        if (existingId) {
          localStorage.removeItem(CART_ID_STORAGE_KEY);
          setCartId(null);
        }
        return;
      }

      if (existingId) {
        cart = await cartLinesUpdate(existingId, lines);
        if (!cart) cart = await cartCreate(lines);
      } else {
        cart = await cartCreate(lines);
      }

      if (cart) {
        setCartId(cart.id);
        setCheckoutUrl(acceptCheckoutUrl(cart.checkoutUrl));
        if (typeof window !== 'undefined') {
          localStorage.setItem(CART_ID_STORAGE_KEY, cart.id);
        }
      }
    } catch (e) {
      console.error('Shopify cart sync error:', e);
    }
  }, []);

  const addItem = useCallback((item: Omit<CartItem, 'quantity'>, quantity: number) => {
    setItems(prev => {
      const existing = prev.find(i => i.variantId === item.variantId);
      let updated: CartItem[];
      if (existing) {
        updated = prev.map(i =>
          i.variantId === item.variantId
            ? { ...i, quantity: Math.min(MAX_CART_QUANTITY, i.quantity + quantity) }
            : i
        );
      } else {
        updated = [...prev, { ...item, quantity }];
      }
      if (isShopifyConnected) syncToShopify(updated);
      return updated;
    });
    setIsOpen(true);
  }, [isShopifyConnected, syncToShopify]);

  const removeItem = useCallback((variantId: string) => {
    setItems(prev => {
      const updated = prev.filter(i => i.variantId !== variantId);
      if (isShopifyConnected) syncToShopify(updated);
      return updated;
    });
  }, [isShopifyConnected, syncToShopify]);

  const updateQuantity = useCallback((variantId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(variantId);
      return;
    }
    setItems(prev => {
      const updated = prev.map(i =>
        i.variantId === variantId ? { ...i, quantity: Math.min(MAX_CART_QUANTITY, quantity) } : i
      );
      if (isShopifyConnected) syncToShopify(updated);
      return updated;
    });
  }, [isShopifyConnected, syncToShopify, removeItem]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        isOpen,
        addItem,
        removeItem,
        updateQuantity,
        openCart,
        closeCart,
        totalItems,
        totalPrice,
        checkoutUrl,
        isShopifyConnected,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
